import React from 'react';
import { makeStyles, Box, Divider, Theme } from '@material-ui/core';
import { EntrepreneurProfileBody, Management } from '../../types/types';
import EditPencil from '../../components/EditPencil';
import { StyledText } from '../../components/StyledText';
import { tbd } from '../../lib/utils';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    minHeight: 1015,
    borderRadius: 8,
    padding: 20,
    [theme.breakpoints.up('md')]: {
      padding: 35
    },
    background: theme.palette.background.paper,
    border: '1px solid #DAD9DF',
    '& h5': {
      marginBottom: 5
    },
    position: 'relative'
  },
  margin: {
    margin: '15px 0'
  }
}));

interface MGMTProps {
  managementTeam: Management[]
}

const ManagementElement: React.FC<MGMTProps> = ({
  managementTeam
// eslint-disable-next-line arrow-body-style
}) => {
  const mgmtArray = managementTeam.map((m) => !!m.title && !!m.name && `${m.title} - ${m.name}`).filter((m) => !!m);
  return (
    <StyledText color='textPrimary' style={{ display: 'block' }}>
      { mgmtArray.length === 0 ? tbd : (
        mgmtArray
          .map((manager, idx) => (
            <span key={`${idx}_${manager}`}>
              {`${manager}`}
              <br />
            </span>
          ))
      ) }
    </StyledText>
  );
};

interface EntCompanyDetailsPanelProps {
  profile: EntrepreneurProfileBody
  editable: boolean
  openEdit?: () => void
}

const EntCompanyDetailsPanel: React.FC<EntCompanyDetailsPanelProps> = ({
  profile, editable, openEdit
}) => {
  const {
    managementTeam,
    bank,
    lawFirm,
    capTableManagement,
    hrProvider,
    fundUsage,
    currentInvestor,
    auditor,
    employeeCount,
    founderExit,
    founderFemale,
    founderUnderrepresented
  } = profile;

  // get boolean string
  const bit2String = (b: boolean) => (b ? 'Yes' : 'No');
  const founderExitString = founderExit !== null ? bit2String(founderExit) : null;
  const founderFemaleString = founderFemale !== null ? bit2String(founderFemale) : null;
  const founderUnderrepresentedString = founderUnderrepresented !== null ? bit2String(founderUnderrepresented) : null;

  const styles = useStyles();
  return (
    <div className={styles.root}>
      {editable && <EditPencil onClick={openEdit} style={{ top: 10, right: 10, position: 'absolute' }} />}
      <Box>
        <Box marginBottom='20px'>
          <StyledText variant='h2'>Company details</StyledText>
        </Box>
        <Box marginBottom='20px'>
          <StyledText variant='h5' color='primary'>Management</StyledText>
          <ManagementElement managementTeam={managementTeam} />
          <Divider className={styles.margin} />
        </Box>
        <Box>
          <StyledText variant='h5' color='primary'>Number of Employees</StyledText>
          <StyledText color={(employeeCount != null) ? 'textPrimary' : 'textSecondary'}>
            { (employeeCount != null) ? employeeCount : tbd}
          </StyledText>
          <Divider className={styles.margin} />
        </Box>
        <Box>
          <StyledText variant='h5' color='primary'>Bank</StyledText>
          <StyledText color={bank ? 'textPrimary' : 'textSecondary'}>{bank || ''}</StyledText>
          <Divider className={styles.margin} />
        </Box>
        <Box>
          <StyledText variant='h5' color='primary'>Auditor/CPA</StyledText>
          <StyledText color={auditor ? 'textPrimary' : 'textSecondary'}>{auditor || ''}</StyledText>
          <Divider className={styles.margin} />
        </Box>
        <Box>
          <StyledText variant='h5' color='primary'>Law Firm</StyledText>
          <StyledText color={lawFirm ? 'textPrimary' : 'textSecondary'}>{lawFirm || ''}</StyledText>
          <Divider className={styles.margin} />
        </Box>
        <Box>
          <StyledText variant='h5' color='primary'>HR Provider</StyledText>
          <StyledText color={hrProvider ? 'textPrimary' : 'textSecondary'}>{hrProvider || ''}</StyledText>
          <Divider className={styles.margin} />
        </Box>
        <Box>
          <StyledText variant='h5' color='primary'>Cap Table Management</StyledText>
          <StyledText color={capTableManagement ? 'textPrimary' : 'textSecondary'}>{capTableManagement || ''}</StyledText>
          <Divider className={styles.margin} />
        </Box>
        <Box>
          <StyledText variant='h5' color='primary'>Current Investor</StyledText>
          <StyledText color={currentInvestor ? 'textPrimary' : 'textSecondary'}>
            {currentInvestor || ''}
          </StyledText>
          <Divider className={styles.margin} />
        </Box>
        <Box>
          <StyledText variant='h5' color='primary'>Use of Funds</StyledText>
          <StyledText color={fundUsage ? 'textPrimary' : 'textSecondary'}>
            {fundUsage || tbd}
          </StyledText>
          <Divider className={styles.margin} />
        </Box>
        <Box>
          <StyledText variant='h5' color='primary'>Founders have made an exit previously</StyledText>
          <StyledText color={founderExitString ? 'textPrimary' : 'textSecondary'}>
            {founderExitString || tbd}
          </StyledText>
          <Divider className={styles.margin} />
        </Box>
        <Box>
          <StyledText variant='h5' color='primary'>Female Founder</StyledText>
          <StyledText color={founderFemaleString ? 'textPrimary' : 'textSecondary'}>
            {founderFemaleString || tbd}
          </StyledText>
          <Divider className={styles.margin} />
        </Box>
        <Box>
          <StyledText variant='h5' color='primary'>Underrepresented Management Team</StyledText>
          <StyledText color={founderUnderrepresentedString ? 'textPrimary' : 'textSecondary'}>
            {founderUnderrepresentedString || tbd}
          </StyledText>
        </Box>
      </Box>
    </div>
  );
};

export default EntCompanyDetailsPanel;
